import BaseLayer from 'ol/layer/Base';

import { getDefaultMap } from 'cadenza/utils/classic-dependency-registry';
import { ClassicMapContext } from 'cadenza/map/context/classic-map-context';
import { WorkbookMapContext } from 'cadenza/map/context/workbook-map-context';
import { LayerContext } from 'cadenza/map/context/layer-context';
import { MapProperty } from 'cadenza/map/map-property';
import { Map } from 'cadenza/map/map';
import { cadenzaLocalStorage } from 'cadenza/utils/storage/storage';
import { LegacyWorkbookMapContext } from 'cadenza/map/context/legacy-workbook-map-context';

import type Layer from 'ol/layer/Base';
import type BaseObject from 'ol/Object';

export type MapContext = WorkbookMapContext | ClassicMapContext;
type ContextType = MapContext | LayerContext;

interface WithCachedContext {
  $cachedContext: ContextType;
}

type SupportedContextObject = (Map | BaseLayer) & WithCachedContext;

export function context (object: Map): MapContext;
export function context (object: BaseLayer): LayerContext;
export function context (object: SupportedContextObject | BaseObject): ContextType | null {
  const contextClass = getClass(object);

  if (contextClass) {
    const supportedObject = object as SupportedContextObject;
    if (!supportedObject.$cachedContext) {
      // Class name is a variable containing the actual class
      // eslint-disable-next-line new-cap
      supportedObject.$cachedContext = new contextClass(supportedObject as unknown as Map & Layer) as ContextType;
    }
    return supportedObject.$cachedContext;
  }

  return null;
}

function getClass<T = BaseObject> (object: T) {
  if (object instanceof Map) {
    if (!object.get(MapProperty.IS_WORKBOOK_MAP)) {
      return ClassicMapContext;
    }
    return cadenzaLocalStorage.getItem('buildMapContextFromWorkbookReduxStore') === 'true'
      ? WorkbookMapContext
      : LegacyWorkbookMapContext;
  }

  if (object instanceof BaseLayer) {
    return LayerContext;
  }

  return null;
}

export function getDefaultMapContext () {
  const map = getDefaultMap();
  return map && context(map);
}
